import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { useState } from 'react'

import type { LedgerBusinessDayRow, LedgerDayStatus } from '@/api/ledger-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { branchDescription } from '@/lib/branch'
import { formatDateTime, formatShortDate } from '@/lib/datetime'

import { DAY_STATUS_LABEL, DAY_STATUS_TONE } from './constants'
import { useBusinessDayRegister, useLedgerBranchReach } from './queries'

/**
 * The register of trading days.
 *
 * The current-day cards answer "is this branch trading"; this answers the questions that come after
 * it — which day was left open, who closed Friday, what they carried forward. It is the read an
 * auditor asks for, and it is the one a branch manager uses on Monday to find the Thursday nobody
 * closed.
 *
 * Server-paginated, like every list in this application: a branch's register outgrows a browser
 * within a year, and ordering or filtering the page that happens to be loaded would be a claim about
 * the whole register that the next page would contradict.
 *
 * Reading it is fine on a phone — the table stacks into cards below `lg` and nothing here keys a
 * value. Closing a day is the part that refuses; that argument lives in `close-business-day.tsx`.
 */

/** The endpoint takes no ordering parameter, so nothing here is sortable. */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const DAY_STATUSES: LedgerDayStatus[] = ['Open', 'Closing', 'Closed']

const ANY = '__any__'

const helper = dataTableColumns<LedgerBusinessDayRow>()

const columns: DataTableColumn<LedgerBusinessDayRow>[] = helper.columns([
  helper.accessor('businessDate', {
    header: 'Business date',
    enableSorting: false,
    cell: (context) => (
      <span className="numeric tabular-nums">
        {formatShortDate(context.row.original.businessDate)}
      </span>
    ),
    meta: { noWrap: true, widthClass: 'w-36', card: 'title' },
  }),
  helper.accessor('branchId', {
    header: 'Branch',
    enableSorting: false,
    // The identifier is the only true label while no module owns branches as records. A bare
    // "Branch" names nothing and an invented name is one the institution never gave.
    cell: (context) => <span className="font-mono text-xs">Branch {context.row.original.branchId}</span>,
    meta: { noWrap: true, widthClass: 'w-28', card: 'lead' },
  }),
  helper.accessor('status', {
    header: 'Status',
    enableSorting: false,
    cell: (context) => (
      <Badge tone={DAY_STATUS_TONE[context.row.original.status]}>
        {DAY_STATUS_LABEL[context.row.original.status]}
      </Badge>
    ),
    meta: { widthClass: 'w-28', card: 'lead' },
  }),
  helper.accessor('openedAtUtc', {
    header: 'Opened',
    enableSorting: false,
    cell: (context) => (
      <span className="numeric text-xs tabular-nums">
        {formatDateTime(context.row.original.openedAtUtc)}
      </span>
    ),
    meta: { noWrap: true },
  }),
  helper.accessor('closedAtUtc', {
    header: 'Closed',
    enableSorting: false,
    // Null while the day is still open, and that is the row worth finding on this screen — so it
    // reads as "still open" rather than as an empty cell somebody has to interpret.
    cell: (context) =>
      context.row.original.closedAtUtc === null ? (
        <span className="text-xs text-warning-text">Still open</span>
      ) : (
        <span className="numeric text-xs tabular-nums">
          {formatDateTime(context.row.original.closedAtUtc)}
        </span>
      ),
    meta: { noWrap: true },
  }),
  helper.accessor('closedByUserId', {
    header: 'Closed by',
    enableSorting: false,
    /*
     * The identifier, because that is all the row carries. No module serves a user directory a
     * ledger role can read, so a name here would have to come from somewhere this screen cannot
     * reach. The audit trail carries the username for whoever holds ops.audit.view.
     */
    cell: (context) =>
      context.row.original.closedByUserId === null ? (
        <span className="text-muted">—</span>
      ) : (
        <span className="font-mono text-xs">user {context.row.original.closedByUserId}</span>
      ),
    meta: { noWrap: true, widthClass: 'w-28' },
  }),
  helper.accessor('carriedForwardNote', {
    header: 'Carried forward',
    enableSorting: false,
    cell: (context) =>
      context.row.original.carriedForwardNote === null ? (
        <span className="text-muted">—</span>
      ) : (
        <span className="text-xs text-body">{context.row.original.carriedForwardNote}</span>
      ),
  }),
])

const DEFAULT_PAGE_SIZE = 50

export function BusinessDayRegister() {
  const reach = useLedgerBranchReach()

  const [branchId, setBranchId] = useState<string | null>(null)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [status, setStatus] = useState<LedgerDayStatus | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const query = useBusinessDayRegister({ branchId, fromDate, toDate, status, page, pageSize })

  const hasFilters = branchId !== null || fromDate !== '' || toDate !== '' || status !== null

  const clearFilters = () => {
    setBranchId(null)
    setFromDate('')
    setToDate('')
    setStatus(null)
    setPage(1)
  }

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater

    setPage(next.pageIndex + 1)
    setPageSize(next.pageSize)
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-3 rounded-card border border-border-subtle bg-surface p-3 lg:grid-cols-4">
        <div className="space-y-1.5">
          <Label htmlFor="register-branch">Branch</Label>
          <Select
            value={branchId ?? ANY}
            onValueChange={(next) => {
              setBranchId(next === ANY ? null : next)
              setPage(1)
            }}
          >
            <SelectTrigger id="register-branch">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Every branch you can reach</SelectItem>
              {reach.branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branchDescription(branch)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="register-from">From</Label>
          <Input
            id="register-from"
            type="date"
            value={fromDate}
            onChange={(event) => {
              setFromDate(event.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="register-to">To</Label>
          <Input
            id="register-to"
            type="date"
            value={toDate}
            onChange={(event) => {
              setToDate(event.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="register-status">Status</Label>
          <Select
            value={status ?? ANY}
            onValueChange={(next) => {
              setStatus(next === ANY ? null : (next as LedgerDayStatus))
              setPage(1)
            }}
          >
            <SelectTrigger id="register-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any status</SelectItem>
              {DAY_STATUSES.map((candidate) => (
                <SelectItem key={candidate} value={candidate}>
                  {DAY_STATUS_LABEL[candidate]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {hasFilters ? (
          <div className="lg:col-span-4">
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear the filters
            </Button>
          </div>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        data={query.data?.items}
        rowCount={query.data?.totalCount}
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={onPaginationChange}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={query.isPending}
        isFetching={query.isFetching}
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
        getRowId={(row) => `${row.branchId}:${row.businessDate}`}
        pageSizeOptions={[25, DEFAULT_PAGE_SIZE, 100, 200]}
        emptyTitle={hasFilters ? 'No days match' : 'No trading days recorded'}
        emptyDescription={
          hasFilters
            ? 'Nothing in the register matches those filters. Widen the dates, or clear them.'
            : 'No branch you can reach has ever opened a trading day. The register fills as days are opened.'
        }
        emptyAction={
          hasFilters ? (
            <Button variant="secondary" size="sm" onClick={clearFilters}>
              Clear the filters
            </Button>
          ) : null
        }
      />
    </div>
  )
}
